import React, { useEffect, useRef, useState } from 'react'
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Collapse,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, Tab, Tabs,
  TextField, Typography,
} from '@mui/material'
import AddIcon         from '@mui/icons-material/Add'
import ExpandMoreIcon  from '@mui/icons-material/ExpandMore'
import ExpandLessIcon  from '@mui/icons-material/ExpandLess'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import OpenInNewIcon    from '@mui/icons-material/OpenInNew'
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'
import { useViewerStudent } from '../../auth/ViewAsContext'
import { readFunctionError } from '../../lib/functionError'
import { Project } from '../../types'
import ConversationThread from '../../components/messaging/ConversationThread'
import Markdown from '../../components/Markdown'
import JournalTab from './JournalTab'
import JudgeReadinessPanel, { JudgeReadinessResult } from '../../components/student/JudgeReadinessPanel'

// ── Types ─────────────────────────────────────────────────────────────────────

type Track  = 'scientific_method' | 'engineering_design'
type Status = 'draft' | 'submitted' | 'approved_draft' | 'completed'
// AI evaluation groups (Tab A: keywords/review_paper/model_paper · Tab B: introduction/background/literature/conclusion)
type Field  = 'keywords' | 'review_paper' | 'model_paper'
            | 'introduction' | 'background' | 'literature' | 'conclusion'

interface AIEvaluation {
  shape:          { state: 'ok' | 'caution' | 'thinking'; note: string }
  anchor:         { state: 'ok' | 'caution' | 'thinking'; note: string }
  suggestions?:   string[]
  ai_disclaimer?: string
}

interface PaperRef       { url?: string; title?: string; abstract?: string }
interface PaperHelp      { queries?: string[]; where?: string[]; what_good_looks_like?: string; tips?: string }
interface NotebookAccess { state: 'ok' | 'warn' | 'unknown'; reason?: string; checked_at?: string }
interface LegacySource   { source?: string; relevance?: string; note?: string }

type PaperSlotKey = 'review_paper' | 'model_paper'

interface ChecklistItem { key: string; label: string; done: boolean }

interface Payload {
  track?:                Track
  // Tab A — Sources
  notebooklm_url?:       string
  notebook_access?:      NotebookAccess
  keywords?:             string[]
  review_paper?:         PaperRef
  model_paper?:          PaperRef
  paper_tries?:          Partial<Record<PaperSlotKey, number>>
  paper_help?:           Partial<Record<PaperSlotKey, PaperHelp>>
  // Tab B — Summary (background-research-paper sections)
  significance?:         string   // 1. Introduction — why it matters
  key_concepts?:         string   // 2. Background & key concepts
  principles?:           string   // 3. Scientific principles — how it works
  what_research_shows?:  string   // 4. Previous research
  the_gap?:              string   // 5. Gaps & future directions
  conclusion?:           string   // 6a. Conclusion — what I learned
  refined_question?:     string   // 6b. Refined research question
  purpose?:              string
  // Paper tab (optional) — AI-assembled draft the student then edits
  paper_draft?:          string
  ai_feedback?:          Partial<Record<Field, AIEvaluation>>
  // Last judge-readiness (winning-bar) result
  judge_readiness?:      JudgeReadinessResult
  // legacy v2 (feasibility moved to Step 4) — kept for read-only display of old rows
  feasibility?:          { iv_levels?: string; dv_measurement?: string; how_works?: string; how_build?: string }
  // legacy v1 (read-only fallback)
  sources?:              LegacySource[]
  synthesis_background?: string
  synthesis_literature?: string
}

interface BackgroundState {
  status:                  Status
  mentor_note:             string | null
  mentor_note_at?:         string | null
  payload:                 Payload | null
  mentor_section_comments?: Record<string, string> | null
  submission_note?:        string | null
}

const NOTEBOOKLM_URL = 'https://notebooklm.google.com'

// ── Page wrapper ────────────────────────────────────────────────────────────────

export default function Step3Page() {
  const { session } = useAuth()
  const { studentId: viewerStudentId, viewerIsStudent, loading: vasLoading } = useViewerStudent()
  const navigate = useNavigate()

  const [project,  setProject]  = useState<Project | null>(null)
  const [track,    setTrack]    = useState<Track>('scientific_method')
  const [step2Q,   setStep2Q]   = useState<string>('')
  const [step2Method, setStep2Method] = useState<string>('')
  const [bg,       setBg]       = useState<BackgroundState | null>(null)
  const [loading,  setLoading]  = useState(true)
  const [tab,      setTab]      = useState<'learn' | 'research' | 'writeup' | 'paper' | 'journal' | 'review'>('research')
  const [researchComplete, setResearchComplete] = useState(false)
  const [summaryComplete,  setSummaryComplete]  = useState(false)
  // Spinner (which unmounts the form) only on first load / actual student
  // switch — never on a same-student refetch, which would discard unsaved input.
  const loadedStudentRef = useRef<string | null>(null)

  useEffect(() => {
    async function load() {
      if (!session || vasLoading) return
      if (!viewerStudentId) { navigate('/'); return }
      if (loadedStudentRef.current !== viewerStudentId) setLoading(true)
      const { data: projects } = await supabase
        .from('project').select('*').eq('student_id', viewerStudentId).eq('status', 'active')
        .order('created_at', { ascending: false }).limit(1)
      const proj = projects?.[0] ?? null
      setProject(proj)

      if (proj) {
        const [{ data: rq }, { data: brRow }] = await Promise.all([
          supabase.from('research_question')
            .select('track_committed, draft_payload').eq('project_id', proj.project_id).maybeSingle(),
          supabase.from('background_research')
            .select('status, mentor_note, mentor_note_at, payload, mentor_section_comments, submission_note').eq('project_id', proj.project_id).maybeSingle(),
        ])
        const tk = ((rq as any)?.track_committed as Track) ?? 'scientific_method'
        setTrack(tk)
        const dp = ((rq as any)?.draft_payload ?? {}) as Record<string, any>
        setStep2Q(tk === 'engineering_design' ? (dp.problem_statement ?? '') : (dp.question ?? ''))
        setStep2Method(dp.method ?? (Array.isArray(dp.method_categories) ? dp.method_categories.join(', ') : ''))
        setBg((brRow as BackgroundState | null) ?? null)
      }
      loadedStudentRef.current = viewerStudentId
      setLoading(false)
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, viewerStudentId, vasLoading])

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  if (!project) return <Box sx={{ p: 4 }}><Typography>No active project found.</Typography></Box>

  const currentStep = project.current_step ?? 1
  if (currentStep < 3) {
    return (
      <Box sx={{ p: 4, maxWidth: 720 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Step 3 · Background Research</Typography>
        <Alert severity="info">Finish Step 2 (your mentor approves your draft) to unlock background research.</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 860 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Step 3 · Background Research</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Learn what's already known about your topic, sharpen your question, and prove your project is doable.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tab label="Learn" value="learn" />
        <Tab label="Sources" value="research" />
        <Tab label="Summary" value="writeup" disabled={!researchComplete} />
        <Tab label="Paper" value="paper" disabled={!summaryComplete} />
        <Tab label="Journal" value="journal" />
        <Tab label="Mentor Review" value="review" />
      </Tabs>

      {/* The work area stays mounted across the three work tabs so in-progress edits
          survive tab switches; Learn / Journal / Mentor Review render on demand. */}
      {tab === 'learn'   && <LearnTab />}
      {tab === 'journal' && (
        <JournalTab projectId={project.project_id} studentId={viewerStudentId!} isEng={track === 'engineering_design'} />
      )}
      {tab === 'review'  && (
        <Step3MentorReviewTab projectId={project.project_id} status={bg?.status ?? 'draft'} mentorNote={bg?.mentor_note ?? null} sectionComments={bg?.mentor_section_comments ?? {}} />
      )}
      <Box sx={{ display: tab === 'research' || tab === 'writeup' || tab === 'paper' ? 'block' : 'none' }}>
        <WorkArea
          projectId={project.project_id}
          studentId={viewerStudentId!}
          track={track}
          step2Q={step2Q}
          step2Method={step2Method}
          initial={bg}
          editable={viewerIsStudent}
          view={tab === 'writeup' ? 'writeup' : tab === 'paper' ? 'paper' : 'research'}
          onContinue={() => setTab('writeup')}
          onGoToPaper={() => setTab('paper')}
          onResearchCompleteChange={setResearchComplete}
          onSummaryCompleteChange={setSummaryComplete}
          onSubmitted={() => setBg(prev => ({ status: 'submitted', mentor_note: null, payload: prev?.payload ?? null }))}
        />
      </Box>
    </Box>
  )
}

// ── Work area (Tab A "Sources" + Tab B "Summary") ──────────────────────────────

function WorkArea({
  projectId, studentId, track, step2Q, step2Method, initial, editable, view,
  onContinue, onGoToPaper, onResearchCompleteChange, onSummaryCompleteChange, onSubmitted,
}: {
  projectId:   string
  studentId:   string
  track:       Track
  step2Q:      string
  step2Method: string
  initial:     BackgroundState | null
  editable:    boolean
  view:        'research' | 'writeup' | 'paper'
  onContinue:  () => void
  onGoToPaper: () => void
  onResearchCompleteChange: (v: boolean) => void
  onSummaryCompleteChange:  (v: boolean) => void
  onSubmitted: () => void
}) {
  const isEng   = track === 'engineering_design'
  const seeded  = initial?.payload ?? {}
  const status  = initial?.status ?? 'draft'
  const mentorComments = initial?.mentor_section_comments ?? {}

  // Tab A state
  const [notebookUrl,    setNotebookUrl]    = useState<string>(seeded.notebooklm_url ?? '')
  const [notebookAccess, setNotebookAccess] = useState<NotebookAccess | null>(seeded.notebook_access ?? null)
  const [checking,       setChecking]       = useState(false)
  const [keywordsText,   setKeywordsText]   = useState<string>((seeded.keywords ?? []).join(', '))
  const [reviewPaper,    setReviewPaper]    = useState<PaperRef>(seeded.review_paper ?? {})
  const [modelPaper,     setModelPaper]     = useState<PaperRef>(seeded.model_paper ?? {})
  const [paperTries,     setPaperTries]     = useState<Partial<Record<PaperSlotKey, number>>>(seeded.paper_tries ?? {})
  const [paperHelp,      setPaperHelp]      = useState<Partial<Record<PaperSlotKey, PaperHelp>>>(seeded.paper_help ?? {})
  const [recommending,   setRecommending]   = useState<PaperSlotKey | null>(null)

  // Tab B state (with v1 → v2 read fallback)
  const [significance, setSignificance] = useState<string>(seeded.significance ?? seeded.synthesis_background ?? '')
  const [keyConcepts,  setKeyConcepts]  = useState<string>(seeded.key_concepts ?? '')
  const [principles,   setPrinciples]   = useState<string>(seeded.principles ?? '')
  const [whatShows,    setWhatShows]    = useState<string>(seeded.what_research_shows ?? seeded.synthesis_literature ?? '')
  const [theGap,       setTheGap]       = useState<string>(seeded.the_gap ?? '')
  const [conclusion,   setConclusion]   = useState<string>(seeded.conclusion ?? '')
  const [refinedQ,     setRefinedQ]     = useState<string>(seeded.refined_question ?? '')
  const [purpose,      setPurpose]      = useState<string>(seeded.purpose ?? '')

  // Paper tab state (optional)
  const [paperDraft,   setPaperDraft]   = useState<string>(seeded.paper_draft ?? '')
  const [generating,   setGenerating]   = useState(false)
  // Cover note to the mentor at submit time — "where should they focus?"
  const [submissionNote, setSubmissionNote] = useState<string>(initial?.submission_note ?? '')

  const [feedback,    setFeedback]    = useState<Payload['ai_feedback']>(seeded.ai_feedback ?? {})
  const [staleSet,    setStaleSet]    = useState<Set<Field>>(new Set())
  const [evaluating,  setEvaluating]  = useState<Field | null>(null)
  const [submitting,  setSubmitting]  = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [softGate,    setSoftGate]    = useState<string[] | null>(null)
  const [contextOpen, setContextOpen] = useState(true)
  const [saveState,   setSaveState]   = useState<'idle' | 'saving' | 'saved'>('idle')
  const [judge,           setJudge]           = useState<JudgeReadinessResult | null>(seeded.judge_readiness ?? null)
  const [judgeLoading,    setJudgeLoading]    = useState(false)

  const isReadOnly  = status === 'submitted' || status === 'approved_draft' || status === 'completed'
  const isReturning = status === 'draft' && !!initial?.mentor_note
  const isApproved  = status === 'approved_draft' || status === 'completed'

  // Notebook-first hard lock: until the share link checks out as publicly accessible,
  // everything else in Step 3 is locked (a mentor who can't open the notebook can't review).
  const notebookOk = notebookAccess?.state === 'ok'
  const locked     = !isReadOnly && !notebookOk

  // Gate to the Background Write-Up tab: the four research items must be in place first.
  function paperReady(p: PaperRef): boolean { return !!(p.url?.trim() && p.abstract?.trim()) }
  const researchChecklist: ChecklistItem[] = [
    { key: 'notebook', label: 'Verify your NotebookLM link',                       done: notebookOk },
    { key: 'keywords', label: 'Add your research keywords',                         done: parseKeywords(keywordsText).length > 0 },
    { key: 'review',   label: 'Add a review / meta-analysis paper (link + abstract)', done: paperReady(reviewPaper) },
    { key: 'model',    label: 'Add a model experimental paper (link + abstract)',     done: paperReady(modelPaper) },
  ]
  const researchComplete = researchChecklist.every(i => i.done)

  // Summary complete → enables the optional Paper tab (which is generated from it).
  const summaryComplete = [significance, keyConcepts, principles, whatShows, theGap, conclusion, refinedQ]
    .every(v => v.trim().length >= 10)

  // Report completion up so the parent can enable the Summary / Paper tabs (Step 1/2 pattern).
  useEffect(() => { onResearchCompleteChange(researchComplete) }, [researchComplete, onResearchCompleteChange])
  useEffect(() => { onSummaryCompleteChange(summaryComplete) }, [summaryComplete, onSummaryCompleteChange])

  function payload(): Payload {
    return {
      track,
      notebooklm_url:      notebookUrl,
      notebook_access:     notebookAccess ?? undefined,
      keywords:            parseKeywords(keywordsText),
      review_paper:        reviewPaper,
      model_paper:         modelPaper,
      paper_tries:         paperTries,
      paper_help:          paperHelp,
      significance,
      key_concepts:        keyConcepts,
      principles,
      what_research_shows: whatShows,
      the_gap:             theGap,
      conclusion,
      refined_question:    refinedQ,
      purpose,
      paper_draft:         paperDraft,
      ai_feedback:         feedback,
      judge_readiness:     judge ?? undefined,
    }
  }

  function markStale(f: Field) {
    setStaleSet(prev => { if (prev.has(f)) return prev; const n = new Set(prev); n.add(f); return n })
  }

  async function persist(extra?: Partial<Payload>) {
    // best-effort; RLS allows the student to write their own row. Does not touch
    // `status`, so an autosave can't advance/regress the review state.
    await supabase.from('background_research').upsert(
      { project_id: projectId, student_id: studentId, payload: { ...payload(), ...extra }, updated_at: new Date().toISOString() },
      { onConflict: 'project_id' },
    )
  }

  // Debounced autosave: persist any edit ~1s after typing stops, so navigating away
  // and back never loses work. Skips read-only rows, viewers, and the initial mount.
  const firstRun = useRef(true)
  useEffect(() => {
    if (isReadOnly || !editable) return
    if (firstRun.current) { firstRun.current = false; return }
    setSaveState('saving')
    const t = setTimeout(async () => {
      try { await persist(); setSaveState('saved') } catch { setSaveState('idle') }
    }, 1000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notebookUrl, notebookAccess, keywordsText, reviewPaper, modelPaper,
      significance, keyConcepts, principles, whatShows, theGap, conclusion, refinedQ, purpose, paperDraft])

  async function checkNotebook() {
    if (checking || !notebookUrl.trim()) return
    setChecking(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step3-check-notebook', {
        body: { url: notebookUrl.trim() },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      const access = data as NotebookAccess
      setNotebookAccess(access)
      await persist({ notebook_access: access })
    } catch (e: any) {
      setError(e.message ?? 'Could not check the link — please try again.')
    } finally {
      setChecking(false)
    }
  }

  async function evaluate(field: Field) {
    if (evaluating) return
    setEvaluating(field); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step3-evaluate-field', {
        body: { project_id: projectId, student_id: studentId, field, payload: payload() },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      const next = { ...(feedback ?? {}), [field]: data.evaluation as AIEvaluation }
      setFeedback(next)
      setStaleSet(prev => { if (!prev.has(field)) return prev; const n = new Set(prev); n.delete(field); return n })
      // Count attempts on the paper slots so we can offer "help me find one" after 2 tries.
      let triesExtra: Partial<Payload> = {}
      if (field === 'review_paper' || field === 'model_paper') {
        const nextTries = { ...paperTries, [field]: (paperTries[field] ?? 0) + 1 }
        setPaperTries(nextTries)
        triesExtra = { paper_tries: nextTries }
      }
      await persist({ ai_feedback: next, ...triesExtra })
    } catch (e: any) {
      setError(e.message ?? 'Could not get AI feedback — please try again.')
    } finally {
      setEvaluating(null)
    }
  }

  // "Judge-readiness" — holistic winning-bar coverage of the whole background
  // against questions judges commonly ask.
  async function runJudgeReadiness() {
    if (judgeLoading) return
    setJudgeLoading(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step3-judge-readiness', {
        body: { project_id: projectId, student_id: studentId, payload: payload() },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      const result = data.readiness as JudgeReadinessResult
      setJudge(result)
      await persist({ judge_readiness: result })
    } catch (e: any) {
      setError(e.message ?? 'Could not run the check — please try again.')
    } finally {
      setJudgeLoading(false)
    }
  }

  async function recommendPaper(slot: PaperSlotKey) {
    if (recommending) return
    setRecommending(slot); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step3-recommend-paper', {
        body: { project_id: projectId, student_id: studentId, kind: slot, payload: payload() },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      const nextHelp = { ...paperHelp, [slot]: data.guidance as PaperHelp }
      setPaperHelp(nextHelp)
      await persist({ paper_help: nextHelp })
    } catch (e: any) {
      setError(e.message ?? 'Could not get a suggestion — please try again.')
    } finally {
      setRecommending(null)
    }
  }

  async function generatePaper() {
    if (generating) return
    setGenerating(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step3-generate-paper', {
        body: { project_id: projectId, student_id: studentId, payload: payload() },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      const text = (data.paper as string) ?? ''
      setPaperDraft(text)
      await persist({ paper_draft: text })
    } catch (e: any) {
      setError(e.message ?? 'Could not generate a draft — please try again.')
    } finally {
      setGenerating(false)
    }
  }

  function readyToSubmit(): boolean {
    if (!notebookOk) return false
    if (!paperReady(reviewPaper) || !paperReady(modelPaper)) return false
    return significance.trim().length >= 10
      && keyConcepts.trim().length >= 10
      && principles.trim().length >= 10
      && whatShows.trim().length >= 10
      && theGap.trim().length >= 10
      && conclusion.trim().length >= 10
      && refinedQ.trim().length >= 10
  }

  const FIELD_LABELS: Record<Field, string> = {
    keywords: 'Keywords', review_paper: 'Review paper', model_paper: 'Model paper',
    introduction: 'Introduction', background: 'Background & principles',
    literature: 'Previous research & gaps', conclusion: 'Conclusion & refined question',
  }

  function thinkingFlags(): string[] {
    const out: string[] = []
    for (const [k, v] of Object.entries(feedback ?? {})) {
      const e = v as AIEvaluation
      if (e?.shape?.state === 'thinking' || e?.anchor?.state === 'thinking') out.push(FIELD_LABELS[k as Field] ?? k)
    }
    return out
  }

  async function submit(force = false) {
    if (submitting || !readyToSubmit()) return
    if (!force) {
      const flags = thinkingFlags()
      if (flags.length > 0) { setSoftGate(flags); return }
    }
    setSubmitting(true); setError(null); setSoftGate(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step3-submit', {
        body: { project_id: projectId, payload: payload(), submission_note: submissionNote },
      })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      if (data?.error) throw new Error(data.error)
      onSubmitted()
    } catch (e: any) {
      setError(e.message ?? 'Could not submit — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 800, pb: 6 }}>
      {!isReadOnly && editable && saveState !== 'idle' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mb: 1 }}>
          {saveState === 'saving' ? 'Saving…' : '✓ All changes saved'}
        </Typography>
      )}

      {/* Status banners (shared across both work tabs) */}
      {isReturning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Your mentor sent this back{initial?.mentor_note_at
            ? ` on ${new Date(initial.mentor_note_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`
            : ''}:</strong>
          {initial?.mentor_note && <Box sx={{ mt: 0.5 }}><Markdown>{initial.mentor_note}</Markdown></Box>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Refine your work and resubmit when ready.
          </Typography>
        </Alert>
      )}
      {status === 'submitted' && (
        <Alert severity="info" sx={{ mb: 2 }}>Submitted to your mentor for review.</Alert>
      )}
      {isApproved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Approved — Step 4 is unlocked. You'll refine your question into a final question + hypothesis there.
        </Alert>
      )}

      {/* Context — Step 2 carry-over (shared) */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardActionArea onClick={() => setContextOpen(o => !o)}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              Where you left off (from Step 2)
            </Typography>
            {contextOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        </CardActionArea>
        <Collapse in={contextOpen}>
          <CardContent sx={{ pt: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
              Your draft {isEng ? 'problem statement' : 'research question'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>{step2Q || '—'}</Typography>
            {step2Method && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Your method approach</Typography>
                <Typography variant="body2">{step2Method}</Typography>
              </>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {view === 'research'
        ? <ResearchView
            isEng={isEng} locked={locked} isReadOnly={isReadOnly}
            notebookUrl={notebookUrl} setNotebookUrl={setNotebookUrl}
            notebookAccess={notebookAccess} checking={checking} onCheck={checkNotebook}
            keywordsText={keywordsText} setKeywordsText={setKeywordsText}
            reviewPaper={reviewPaper} setReviewPaper={setReviewPaper}
            modelPaper={modelPaper} setModelPaper={setModelPaper}
            paperTries={paperTries} paperHelp={paperHelp} recommending={recommending} onRecommend={recommendPaper}
            researchComplete={researchComplete} researchChecklist={researchChecklist} onContinue={onContinue}
            feedback={feedback} staleSet={staleSet} evaluating={evaluating}
            onEvaluate={evaluate} markStale={markStale}
          />
        : view === 'writeup' ? <WriteUpView
            isEng={isEng} isReadOnly={isReadOnly}
            mentorComments={mentorComments}
            significance={significance} setSignificance={setSignificance}
            keyConcepts={keyConcepts} setKeyConcepts={setKeyConcepts}
            principles={principles} setPrinciples={setPrinciples}
            whatShows={whatShows} setWhatShows={setWhatShows}
            theGap={theGap} setTheGap={setTheGap}
            conclusion={conclusion} setConclusion={setConclusion}
            refinedQ={refinedQ} setRefinedQ={setRefinedQ}
            purpose={purpose} setPurpose={setPurpose}
            feedback={feedback} staleSet={staleSet} evaluating={evaluating}
            onEvaluate={evaluate} markStale={markStale}
          />
        : <PaperView
            isReadOnly={isReadOnly} editable={editable}
            paperDraft={paperDraft} setPaperDraft={setPaperDraft}
            generating={generating} onGenerate={generatePaper}
            isReturning={isReturning}
            canSubmit={readyToSubmit()} submitting={submitting}
            onSubmit={() => submit(false)}
            submissionNote={submissionNote} setSubmissionNote={setSubmissionNote}
          />}

      {view === 'writeup' && (
        <JudgeReadinessPanel
          description="This looks at your whole background research together and scores how much of it could stand up to the questions science-fair judges commonly ask — so you can strengthen the research (not memorize answers)."
          subject="background"
          canRun={!!refinedQ.trim() && !isReadOnly}
          hint={!refinedQ.trim() && !isReadOnly ? 'Write your refined research question (in the Conclusion) first.' : null}
          loading={judgeLoading}
          onRun={runJudgeReadiness}
          result={judge}
        />
      )}

      {/* Last step — generate the draft paper. Placed after the checks so students
          strengthen their research before turning it into a paper. */}
      {view === 'writeup' && !isReadOnly && editable && (
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'flex-start' }}>
            Last step — once your research is as strong as you can make it, turn it into a draft paper.
          </Typography>
          {!readyToSubmit() && (
            <Typography variant="caption" color="text.secondary">
              Complete all the sections above to continue.
            </Typography>
          )}
          <Button
            variant="contained"
            disabled={!readyToSubmit() || generating}
            onClick={() => { onGoToPaper(); if (!paperDraft.trim()) generatePaper() }}
          >
            {generating ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Writing your draft…</>
              : 'Generate Draft Background Research Paper'}
          </Button>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

      <Dialog open={!!softGate} onClose={() => setSoftGate(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit anyway?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>AI flagged a few things to think about on:</Typography>
          <Box component="ul" sx={{ pl: 2.25, m: 0, mb: 1.5 }}>
            {(softGate ?? []).map(l => <li key={l}><Typography variant="body2">{l}</Typography></li>)}
          </Box>
          <Typography variant="body2" color="text.secondary">
            You can submit anyway — your mentor will review your work regardless. Or refine first if you want to address these.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSoftGate(null)} disabled={submitting}>Refine first</Button>
          <Button variant="contained" onClick={() => submit(true)} disabled={submitting}>Submit anyway</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ── Tab A · Sources ──────────────────────────────────────────────────────────────

function ResearchView(props: {
  isEng: boolean; locked: boolean; isReadOnly: boolean
  notebookUrl: string; setNotebookUrl: (v: string) => void
  notebookAccess: NotebookAccess | null; checking: boolean; onCheck: () => void
  keywordsText: string; setKeywordsText: (v: string) => void
  reviewPaper: PaperRef; setReviewPaper: (v: PaperRef) => void
  modelPaper: PaperRef; setModelPaper: (v: PaperRef) => void
  paperTries: Partial<Record<PaperSlotKey, number>>; paperHelp: Partial<Record<PaperSlotKey, PaperHelp>>
  recommending: PaperSlotKey | null; onRecommend: (slot: PaperSlotKey) => void
  researchComplete: boolean; researchChecklist: ChecklistItem[]; onContinue: () => void
  feedback: Payload['ai_feedback']; staleSet: Set<Field>; evaluating: Field | null
  onEvaluate: (f: Field) => void; markStale: (f: Field) => void
}) {
  const {
    locked, isReadOnly, notebookUrl, setNotebookUrl, notebookAccess, checking, onCheck,
    keywordsText, setKeywordsText, reviewPaper, setReviewPaper, modelPaper, setModelPaper,
    paperTries, paperHelp, recommending, onRecommend,
    researchComplete, researchChecklist, onContinue,
    feedback, staleSet, evaluating, onEvaluate, markStale,
  } = props
  const access = notebookAccess?.state

  return (
    <Box sx={{ maxWidth: 800 }}>
      {/* 1 · NotebookLM — the hard gate */}
      <SectionHeading n={1} title="Set up & share your notebook" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
        Create a notebook in <strong>NotebookLM</strong> — this is your research workspace where your sources live.
        Set sharing to <strong>"Anyone with the link"</strong> so your mentor can open it, then paste the share link and verify it.
      </Typography>
      <Button sx={{ mb: 2 }} variant="outlined" size="small" endIcon={<OpenInNewIcon />}
        href={NOTEBOOKLM_URL} target="_blank" rel="noopener noreferrer">
        Open NotebookLM
      </Button>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
        <TextField
          fullWidth size="small" type="url" label="NotebookLM share link"
          placeholder="https://notebook.google.com/notebook/…"
          value={notebookUrl} onChange={e => setNotebookUrl(e.target.value)} disabled={isReadOnly}
        />
        {!isReadOnly && (
          <Button variant="contained" onClick={onCheck} disabled={checking || !notebookUrl.trim()} sx={{ whiteSpace: 'nowrap', height: 40 }}>
            {checking ? <CircularProgress size={16} color="inherit" /> : 'Verify link'}
          </Button>
        )}
      </Box>
      {access === 'ok' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {notebookAccess?.reason ?? 'Looks accessible — your mentor will be able to open this notebook.'}
        </Alert>
      )}
      {access === 'warn' && (
        <Alert severity="warning" sx={{ mb: 3 }}>{notebookAccess?.reason}</Alert>
      )}
      {isReadOnly && access !== 'ok' && notebookUrl && (
        <Box sx={{ mb: 3 }}><a href={notebookUrl} target="_blank" rel="noopener noreferrer">{notebookUrl}</a></Box>
      )}

      {locked ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, borderRadius: 1, bgcolor: 'action.hover', color: 'text.secondary' }}>
          <LockOutlinedIcon fontSize="small" />
          <Typography variant="body2">
            Verify your notebook link above to unlock the rest of Step 3. Be sure you've shared it ("Anyone with the link") so your mentor can open it.
          </Typography>
        </Box>
      ) : (
        <>
          <Divider sx={{ my: 3 }} />

          {/* 2 · Keywords */}
          <SectionHeading n={2} title="Research keywords" />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            List the search terms you'll use to find sources about your question — the key topics, variables, and their synonyms.
            Separate them with commas or new lines. Get AI feedback to see what you're missing.
          </Typography>
          <KeywordBlock value={keywordsText} onChange={v => { setKeywordsText(v); markStale('keywords') }} disabled={isReadOnly}
            suggestions={staleSet.has('keywords') ? [] : (feedback?.keywords?.suggestions ?? [])} />
          <EvalRow field="keywords" feedback={feedback?.keywords} stale={staleSet.has('keywords')}
            evaluating={evaluating === 'keywords'} onEvaluate={() => onEvaluate('keywords')}
            disabled={isReadOnly || parseKeywords(keywordsText).length === 0} />

          <Divider sx={{ my: 3 }} />

          {/* 3 · Review paper */}
          <SectionHeading n={3} title="★ Review / meta-analysis paper" />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Find one <strong>review or meta-analysis</strong> in your field — a paper that summarizes <em>many</em> studies at once.
            It gives you the lay of the land. Paste its link, title, and abstract so AI can check it's a good fit.
          </Typography>
          <PaperSlot value={reviewPaper} onChange={next => { setReviewPaper(next); markStale('review_paper') }} disabled={isReadOnly} />
          <EvalRow field="review_paper" feedback={feedback?.review_paper} stale={staleSet.has('review_paper')}
            evaluating={evaluating === 'review_paper'} onEvaluate={() => onEvaluate('review_paper')}
            disabled={isReadOnly || !(reviewPaper.title?.trim() || reviewPaper.abstract?.trim())} />
          <PaperHelpRow
            show={(paperTries.review_paper ?? 0) >= 2 && isFailing(feedback?.review_paper) && !staleSet.has('review_paper')}
            help={paperHelp.review_paper} loading={recommending === 'review_paper'}
            onRecommend={() => onRecommend('review_paper')} disabled={isReadOnly} />

          <Divider sx={{ my: 3 }} />

          {/* 4 · Model experimental paper */}
          <SectionHeading n={4} title="★ Model experimental paper" />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Find one <strong>experimental paper</strong> — a single study with a methods + results section — whose structure you can
            emulate in your own experimental design. Paste its link, title, and abstract.
          </Typography>
          <PaperSlot value={modelPaper} onChange={next => { setModelPaper(next); markStale('model_paper') }} disabled={isReadOnly} />
          <EvalRow field="model_paper" feedback={feedback?.model_paper} stale={staleSet.has('model_paper')}
            evaluating={evaluating === 'model_paper'} onEvaluate={() => onEvaluate('model_paper')}
            disabled={isReadOnly || !(modelPaper.title?.trim() || modelPaper.abstract?.trim())} />
          <PaperHelpRow
            show={(paperTries.model_paper ?? 0) >= 2 && isFailing(feedback?.model_paper) && !staleSet.has('model_paper')}
            help={paperHelp.model_paper} loading={recommending === 'model_paper'}
            onRecommend={() => onRecommend('model_paper')} disabled={isReadOnly} />

          <Alert severity="info" variant="outlined" sx={{ mt: 3 }}>
            Add every other source you read to your <strong>NotebookLM</strong> notebook — your mentor can open it to see them.
            Only these two key papers are captured here.
          </Alert>

          {!isReadOnly && (
            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
              {!researchComplete && (
                <Typography variant="caption" color="text.secondary">
                  {researchChecklist.filter(i => i.done).length} of {researchChecklist.length} done — finish all four to continue.
                </Typography>
              )}
              <Button variant="contained" onClick={onContinue} disabled={!researchComplete}>
                Continue to Summary
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

// ── Tab B · Summary (background-research-paper sections) ─────────────────────────

// Read-only mentor comment shown to the student under a section's AI feedback.
// Mentors write these on the review page; they augment (don't replace) AI feedback.
function MentorComment({ text }: { text?: string }) {
  if (!text?.trim()) return null
  return (
    <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'primary.light', bgcolor: 'rgba(15,43,76,0.04)' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 0.5 }}>
        Mentor comment
      </Typography>
      <Markdown>{text}</Markdown>
    </Box>
  )
}

function WriteUpView(props: {
  isEng: boolean; isReadOnly: boolean
  mentorComments: Record<string, string>
  significance: string; setSignificance: (v: string) => void
  keyConcepts: string; setKeyConcepts: (v: string) => void
  principles: string; setPrinciples: (v: string) => void
  whatShows: string; setWhatShows: (v: string) => void
  theGap: string; setTheGap: (v: string) => void
  conclusion: string; setConclusion: (v: string) => void
  refinedQ: string; setRefinedQ: (v: string) => void
  purpose: string; setPurpose: (v: string) => void
  feedback: Payload['ai_feedback']; staleSet: Set<Field>; evaluating: Field | null
  onEvaluate: (f: Field) => void; markStale: (f: Field) => void
}) {
  const {
    isEng, isReadOnly, mentorComments,
    significance, setSignificance, keyConcepts, setKeyConcepts, principles, setPrinciples,
    whatShows, setWhatShows, theGap, setTheGap, conclusion, setConclusion,
    refinedQ, setRefinedQ, purpose, setPurpose,
    feedback, staleSet, evaluating, onEvaluate, markStale,
  } = props

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
        Pull together what your research found, in your own words. These are the key parts of a background-research paper —
        and they lead to a sharper research question that defines your project for Step 4. Get AI feedback on each part as you go.
      </Typography>

      {/* 1 · Introduction */}
      <SectionHeading n={1} title="Introduction" />
      <DraftField
        label="Why this problem is important"
        helperText="Introduce your topic and give a real reason it matters — to you, to science, or to the world."
        value={significance} onChange={v => { setSignificance(v); markStale('introduction') }} disabled={isReadOnly} rows={3}
      />
      <EvalRow field="introduction" feedback={feedback?.introduction} stale={staleSet.has('introduction')}
        evaluating={evaluating === 'introduction'} onEvaluate={() => onEvaluate('introduction')}
        disabled={isReadOnly || !significance.trim()} />
      <MentorComment text={mentorComments.introduction} />

      <Divider sx={{ my: 3 }} />

      {/* 2 · Background & key concepts + 3 · Scientific principles */}
      <SectionHeading n={2} title="Background & scientific principles" />
      <DraftField
        label="Background & key concepts"
        helperText="The concepts and terminology someone needs to understand your project, in your own words."
        value={keyConcepts} onChange={v => { setKeyConcepts(v); markStale('background') }} disabled={isReadOnly} rows={4}
      />
      <DraftField
        label="Scientific principles"
        helperText="How does the system work? What mechanisms or scientific principles are involved?"
        value={principles} onChange={v => { setPrinciples(v); markStale('background') }} disabled={isReadOnly} rows={4}
      />
      <EvalRow field="background" feedback={feedback?.background} stale={staleSet.has('background')}
        evaluating={evaluating === 'background'} onEvaluate={() => onEvaluate('background')}
        disabled={isReadOnly || (!keyConcepts.trim() && !principles.trim())} />
      <MentorComment text={mentorComments.background} />

      <Divider sx={{ my: 3 }} />

      {/* 4 · Previous research + 5 · Gaps & future directions */}
      <SectionHeading n={3} title="Previous research & gaps" />
      <DraftField
        label="What other researchers have found"
        helperText="Summarize what your sources discovered — the consensus. Note which source each point comes from."
        value={whatShows} onChange={v => { setWhatShows(v); markStale('literature') }} disabled={isReadOnly} rows={4}
      />
      <DraftField
        label="Gaps & future directions"
        helperText="What's still uncertain, controversial, or unexplored — and where is the field heading? Name the opening your project addresses."
        value={theGap} onChange={v => { setTheGap(v); markStale('literature') }} disabled={isReadOnly} rows={4}
      />
      <EvalRow field="literature" feedback={feedback?.literature} stale={staleSet.has('literature')}
        evaluating={evaluating === 'literature'} onEvaluate={() => onEvaluate('literature')}
        disabled={isReadOnly || (!whatShows.trim() && !theGap.trim())} />
      <MentorComment text={mentorComments.literature} />

      <Divider sx={{ my: 3 }} />

      {/* 6 · Conclusion → refined research question */}
      <SectionHeading n={4} title="Conclusion — your refined research question" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
        This is what your background research adds up to: what you learned, and a sharper {isEng ? 'problem statement' : 'research question'}
        {' '}that defines your project. It's still a draft — you'll lock the final version (with your hypothesis) in Step 4.
      </Typography>
      <DraftField
        label="What you learned from the literature"
        helperText="In a few sentences: what do you now understand that you didn't before?"
        value={conclusion} onChange={v => { setConclusion(v); markStale('conclusion') }} disabled={isReadOnly} rows={3}
      />
      <DraftField
        label={isEng ? 'Your refined problem statement' : 'Your refined research question'}
        helperText="Based on what you learned — sharper and more specific than your Step 2 draft."
        value={refinedQ} onChange={v => { setRefinedQ(v); markStale('conclusion') }} disabled={isReadOnly} rows={3}
      />
      <DraftField
        label="Purpose statement"
        helperText="Start with: 'The purpose of my research is to…'"
        value={purpose} onChange={v => { setPurpose(v); markStale('conclusion') }} disabled={isReadOnly} rows={2}
      />
      <EvalRow field="conclusion" feedback={feedback?.conclusion} stale={staleSet.has('conclusion')}
        evaluating={evaluating === 'conclusion'} onEvaluate={() => onEvaluate('conclusion')}
        disabled={isReadOnly || !refinedQ.trim()} />
      <MentorComment text={mentorComments.conclusion} />
    </Box>
  )
}

// ── Tab Paper · optional draft-paper practice ────────────────────────────────────

function printPaper(body: string) {
  const w = window.open('', '_blank', 'width=820,height=1000')
  if (!w) return
  const safe = body.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string))
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>Background Research Paper</title>` +
    `<style>body{font-family:Georgia,'Times New Roman',serif;line-height:1.6;max-width:720px;` +
    `margin:40px auto;padding:0 24px;color:#111}pre{white-space:pre-wrap;word-wrap:break-word;` +
    `font-family:inherit;font-size:14px}@media print{body{margin:0}}</style></head>` +
    `<body><pre>${safe}</pre></body></html>`,
  )
  w.document.close(); w.focus()
  setTimeout(() => w.print(), 250)
}

function PaperView({
  isReadOnly, editable, paperDraft, setPaperDraft, generating, onGenerate,
  isReturning, canSubmit, submitting, onSubmit, submissionNote, setSubmissionNote,
}: {
  isReadOnly: boolean; editable: boolean
  paperDraft: string; setPaperDraft: (v: string) => void
  generating: boolean; onGenerate: () => void
  isReturning: boolean; canSubmit: boolean; submitting: boolean; onSubmit: () => void
  submissionNote: string; setSubmissionNote: (v: string) => void
}) {
  const has = !!paperDraft.trim()
  function regenerate() {
    if (paperDraft.trim() && !window.confirm('Replace the current draft with a fresh AI version? Your edits will be lost.')) return
    onGenerate()
  }
  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
        Your Summary is assembled into a draft background-research paper here. The AI organizes <strong>your own Summary
        notes</strong> into a first draft — it won't add new facts or sources. Edit it into your own voice, then submit it
        for mentor review and print a copy for your notebook.
      </Typography>

      {!has ? (
        <Box sx={{ p: 3, borderRadius: 1, bgcolor: 'action.hover', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When you're ready, generate a first draft from everything you wrote in the Summary.
          </Typography>
          <Button variant="contained" onClick={onGenerate} disabled={generating || isReadOnly}>
            {generating ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Writing your draft…</>
              : 'Generate draft paper'}
          </Button>
        </Box>
      ) : (
        <>
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            This is an AI-assembled <strong>first draft</strong> from your Summary. Make it yours — edit, reword, and fix
            anything before you submit or print.
          </Alert>
          <TextField
            fullWidth multiline minRows={18} value={paperDraft}
            onChange={e => setPaperDraft(e.target.value)} disabled={isReadOnly}
            sx={{ '& textarea': { fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.6 } }}
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {!isReadOnly && (
              <Button variant="outlined" onClick={regenerate} disabled={generating}>
                {generating ? <><CircularProgress size={14} sx={{ mr: 1 }} />Regenerating…</> : 'Regenerate'}
              </Button>
            )}
            <Button variant="outlined" onClick={() => printPaper(paperDraft)}>Print for notebook</Button>
          </Box>

          {/* Cover note to the mentor — tells them where to focus this cycle */}
          {!isReadOnly && editable && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Note to your mentor <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {isReturning
                  ? 'What did you change since last time, and where would you like feedback?'
                  : 'What are you most unsure about, or where would you like your mentor to focus?'}
              </Typography>
              <TextField
                fullWidth multiline minRows={2} maxRows={8} size="small"
                placeholder="e.g. I reworked my conclusion to tie the studies together — is the gap clear? Not sure my question is testable yet."
                value={submissionNote} onChange={e => setSubmissionNote(e.target.value)}
              />
            </Box>
          )}

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }} />
            {!isReadOnly && editable && (
              <Button variant="contained" disabled={!canSubmit || submitting} onClick={onSubmit}>
                {submitting ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Submitting…</>
                  : isReturning ? 'Resubmit for mentor review' : 'Submit for mentor review'}
              </Button>
            )}
            {/* Staff in View-As can't submit on the student's behalf — show a
                disabled button so it's clear where the student submits from. */}
            {!isReadOnly && !editable && (
              <Button variant="contained" disabled>
                {isReturning ? 'Resubmit for mentor review' : 'Submit for mentor review'}
              </Button>
            )}
          </Box>
          {!isReadOnly && !editable && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
              Only the student can submit for mentor review — they'll do it from here.
            </Typography>
          )}
        </>
      )}
    </Box>
  )
}

// ── Tab Mentor Review (status + send-back note + conversation) — mirrors Step 2 ───

const S3_SECTION_LABELS: Record<string, string> = {
  introduction: 'Introduction', background: 'Background', literature: 'Literature', conclusion: 'Conclusion',
}

function Step3MentorReviewTab({ projectId, status, mentorNote, sectionComments }: {
  projectId: string; status: Status; mentorNote: string | null; sectionComments: Record<string, string>
}) {
  const sections = Object.entries(sectionComments).filter(([, v]) => (v ?? '').trim())
  const isPending = status === 'submitted'
  const isApproved = status === 'approved_draft' || status === 'completed'
  const hasNote   = !!mentorNote && (status === 'draft' || isApproved)
  const headline =
    status === 'approved_draft' ? 'Approved — Step 4 unlocks' :
    status === 'completed'      ? 'Approved' :
    status === 'submitted'      ? 'Awaiting mentor review' :
    hasNote                     ? 'Sent back with comment' :
                                  'Not yet submitted'
  return (
    <Box sx={{ maxWidth: 760, py: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Mentor Review</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track your mentor's review of your Step 3 background research and exchange messages while it's pending.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Status
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{headline}</Typography>
        </CardContent>
      </Card>

      {(hasNote || (isApproved && sections.length > 0)) && (
        <Card variant="outlined" sx={{ mb: 3, borderColor: isApproved ? 'success.light' : 'warning.light' }}>
          <CardContent>
            <Typography variant="caption" color={isApproved ? 'success.main' : 'warning.main'} sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Mentor's note
            </Typography>
            {mentorNote && <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>{mentorNote}</Typography>}
            {isApproved && sections.map(([key, comment]) => (
              <Box key={key} sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{S3_SECTION_LABELS[key] ?? key}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Conversation with your mentor</Typography>
          <ConversationThread projectId={projectId} editable={isPending} viewerSide="student" />
        </CardContent>
      </Card>
    </Box>
  )
}

// ── Learn tab (NotebookLM workflow + Lesson 3/4 teaching videos) ──────────────────

const LEARN_SECTIONS = [
  {
    title: 'Finding good sources',
    intro: 'Strong background research starts with finding credible, relevant sources — and knowing which kind of paper does which job for you.',
    videos: [
      { title: 'Identify Keywords & Search Terms',             videoId: '852010040' },
      { title: 'Find Credible, Reliable Sources',              videoId: '852010070' },
      { title: 'Literature Reviews vs. Experimental Papers',   videoId: '852010121' },
    ],
  },
  {
    title: 'Understanding and synthesizing',
    intro: 'Reading is only half the work — the goal is to make sense of what you find, let it sharpen your question, and recognize a model paper you can learn from.',
    videos: [
      { title: 'Read & Synthesize Information',  videoId: '852010196' },
      { title: 'Refine Your Research Question',  videoId: '852010270' },
      { title: 'Selecting a Model Paper',        videoId: '852010290' },
    ],
  },
]

function VimeoEmbed({ videoId, title }: { videoId: string; title: string }) {
  return (
    <Box sx={{ mt: 2, textAlign: 'center' }}>
      <iframe
        title={title}
        src={`https://player.vimeo.com/video/${videoId}?title=0&portrait=0&byline=0`}
        width="640"
        height="360"
        style={{ border: 'none', maxWidth: '100%' }}
        allow="autoplay; fullscreen"
        allowFullScreen
      />
    </Box>
  )
}

function LearnTab() {
  const [openVideo, setOpenVideo] = useState<string | null>(null)
  return (
    <Box sx={{ maxWidth: 760, py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
        Background research means finding out what's already known about your topic so your project builds on real
        knowledge — and so you can prove it's doable. You'll organize your reading in <strong>NotebookLM</strong>, a tool
        that answers questions <em>grounded only in the sources you give it, with citations</em>. It can't make things up,
        so it's a safe research partner: it helps you understand faster, but <strong>you</strong> do the reading and the thinking.
      </Typography>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>The workflow</Typography>
          <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75, lineHeight: 1.6 } }}>
            <li>Create a notebook in NotebookLM and share it ("Anyone with the link") — paste the link in <strong>Sources</strong>.</li>
            <li>List your search keywords, then use them to find sources.</li>
            <li>Pick a review/meta-analysis paper and a model experimental paper — add all your sources to the notebook.</li>
            <li>Use the Audio Overview / chat to understand each source.</li>
            <li>Write up what you learned in <strong>Summary</strong>, in your own words.</li>
            <li>Answer the feasibility check: exactly how you'll run or build your project.</li>
          </Box>
          <Button sx={{ mt: 2 }} variant="contained" size="small" endIcon={<OpenInNewIcon />}
            href={NOTEBOOKLM_URL} target="_blank" rel="noopener noreferrer">
            Open NotebookLM
          </Button>
        </CardContent>
      </Card>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Lesson videos</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
        Short lessons that walk through each part of background research. Watch the ones that match what you're working on.
      </Typography>
      {LEARN_SECTIONS.map((section, si) => (
        <Box key={si} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {si + 1}. {section.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
            {section.intro}
          </Typography>
          {section.videos.map(v => (
            <Card key={v.videoId} variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: '12px !important' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                  onClick={() => setOpenVideo(openVideo === v.videoId ? null : v.videoId)}
                >
                  <PlayCircleOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{v.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {openVideo === v.videoId ? 'Hide' : 'Watch'}
                  </Typography>
                </Box>
                {openVideo === v.videoId && <VimeoEmbed videoId={v.videoId} title={v.title} />}
              </CardContent>
            </Card>
          ))}
          {si < LEARN_SECTIONS.length - 1 && <Divider sx={{ mt: 2.5 }} />}
        </Box>
      ))}
    </Box>
  )
}

// ── Subcomponents ─────────────────────────────────────────────────────────────

function SectionHeading({ n, title }: { n: number; title: string }) {
  return <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{n}. {title}</Typography>
}

// Parse a free-text keyword block (commas or new lines) into a deduped term list.
function parseKeywords(text: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of text.split(/[,\n]/)) {
    const t = raw.trim()
    if (!t) continue
    const k = t.toLowerCase()
    if (seen.has(k)) continue
    seen.add(k); out.push(t)
  }
  return out
}

function KeywordBlock({ value, onChange, disabled, suggestions }: {
  value: string; onChange: (v: string) => void; disabled: boolean; suggestions: string[]
}) {
  const existing = new Set(parseKeywords(value).map(k => k.toLowerCase()))
  const newSuggestions = suggestions.filter(s => s.trim() && !existing.has(s.trim().toLowerCase()))
  function add(term: string) {
    const t = term.trim()
    if (!t || existing.has(t.toLowerCase())) return
    const base = value.replace(/[\s,]*$/, '')
    onChange(base ? `${base}, ${t}` : t)
  }
  return (
    <Box sx={{ mb: 1.5 }}>
      <TextField
        fullWidth multiline rows={2} size="small" label="Keywords"
        placeholder="e.g. soil moisture, plant growth, drought tolerance, transpiration"
        helperText="Separate terms with commas or new lines."
        value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      />
      {!disabled && newSuggestions.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            AI suggested — tap to add:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {newSuggestions.map((s, i) => (
              <Chip key={i} label={s} size="small" variant="outlined" icon={<AddIcon />} onClick={() => add(s)} sx={{ cursor: 'pointer' }} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

// A paper "fails" a trial when AI flags its type or relevance as needing a rethink.
function isFailing(fb: AIEvaluation | undefined): boolean {
  return !!fb && (fb.shape?.state === 'thinking' || fb.anchor?.state === 'thinking')
}

function PaperHelpRow({ show, help, loading, onRecommend, disabled }: {
  show: boolean; help?: PaperHelp; loading: boolean; onRecommend: () => void; disabled: boolean
}) {
  if (!show && !help) return null
  return (
    <Box sx={{ mt: 1 }}>
      {!disabled && (
        <Button size="small" variant="contained" color="secondary" onClick={onRecommend} disabled={loading}>
          {loading ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Finding…</>
            : help ? 'Refresh search help' : '✨ Help me find one (AI)'}
        </Button>
      )}
      {help && <PaperHelpPanel help={help} />}
    </Box>
  )
}

function PaperHelpPanel({ help }: { help: PaperHelp }) {
  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'rgba(2,136,209,0.06)', border: '1px solid', borderColor: 'rgba(2,136,209,0.3)' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>Here's how to find a good one</Typography>
      {(help.queries ?? []).length > 0 && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Try these searches:</Typography>
          <Box component="ul" sx={{ pl: 2.25, m: 0, mb: 1 }}>
            {help.queries!.map((q, i) => (
              <li key={i}><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{q}</Typography></li>
            ))}
          </Box>
        </>
      )}
      {(help.where ?? []).length > 0 && (
        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Where to look:</strong> {help.where!.join(' · ')}</Typography>
      )}
      {help.what_good_looks_like && (
        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>A good fit:</strong> {help.what_good_looks_like}</Typography>
      )}
      {help.tips && <Typography variant="body2" color="text.secondary">{help.tips}</Typography>}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.75 }}>
        Find the paper, read it, then paste it above — the AI can't open links, so you choose the final one.
      </Typography>
    </Box>
  )
}

function PaperSlot({ value, onChange, disabled }: {
  value: PaperRef; onChange: (next: PaperRef) => void; disabled: boolean
}) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1.5 }}>
      <TextField fullWidth size="small" type="url" label="Link to the paper" value={value.url ?? ''}
        onChange={e => onChange({ ...value, url: e.target.value })} disabled={disabled} sx={{ mb: 1 }} />
      <TextField fullWidth size="small" label="Paper title" value={value.title ?? ''}
        onChange={e => onChange({ ...value, title: e.target.value })} disabled={disabled} sx={{ mb: 1 }} />
      <TextField fullWidth size="small" multiline rows={4} label="Abstract (paste it from the paper)"
        helperText="Copy the paper's abstract here so AI can check it's a good fit."
        value={value.abstract ?? ''} onChange={e => onChange({ ...value, abstract: e.target.value })} disabled={disabled} />
    </Box>
  )
}

function DraftField({ label, helperText, value, onChange, disabled, rows = 3 }: {
  label: string; helperText: string; value: string; onChange: (v: string) => void; disabled: boolean; rows?: number
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <TextField fullWidth multiline rows={rows} size="small" label={label} value={value}
        onChange={e => onChange(e.target.value)} helperText={helperText} disabled={disabled} />
    </Box>
  )
}

function EvalRow({ field, feedback, stale, evaluating, onEvaluate, disabled }: {
  field: Field; feedback: AIEvaluation | undefined; stale: boolean
  evaluating: boolean; onEvaluate: () => void; disabled: boolean
}) {
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" variant="outlined" onClick={onEvaluate} disabled={evaluating || disabled}>
          {evaluating ? <><CircularProgress size={14} sx={{ mr: 1 }} />Evaluating…</>
            : feedback ? 'Refresh AI feedback' : 'Get AI feedback'}
        </Button>
        {stale && feedback && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Feedback is stale — re-check?</Typography>
        )}
      </Box>
      {feedback && <FeedbackBlock feedback={feedback} stale={stale} />}
    </Box>
  )
}

function FeedbackBlock({ feedback, stale }: { feedback: AIEvaluation; stale: boolean }) {
  const dims: Array<{ key: 'shape' | 'anchor'; label: string }> = [
    { key: 'shape', label: 'Shape' }, { key: 'anchor', label: 'Anchor' },
  ]
  const color = (s: string) => s === 'ok' ? 'success.main' : s === 'caution' ? 'warning.main' : 'error.main'
  const bgc   = (s: string) => s === 'ok' ? 'rgba(46,125,50,0.08)' : s === 'caution' ? 'rgba(237,108,2,0.08)' : 'rgba(198,40,40,0.08)'
  const glyph = (s: string) => s === 'ok' ? '✓' : s === 'caution' ? '⚠' : '?'
  return (
    <Box sx={{ mt: 1.5, opacity: stale ? 0.55 : 1 }}>
      {dims.map(d => {
        const dim = feedback[d.key]; if (!dim) return null
        return (
          <Box key={d.key} sx={{ display: 'flex', gap: 1, p: 1.25, mb: 0.75, borderRadius: 1, bgcolor: bgc(dim.state), border: '1px solid', borderColor: `${color(dim.state)}40` }}>
            <Typography sx={{ color: color(dim.state), fontWeight: 700, minWidth: 18, textAlign: 'center' }}>{glyph(dim.state)}</Typography>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: color(dim.state), display: 'block' }}>{d.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{dim.note}</Typography>
            </Box>
          </Box>
        )
      })}
      {feedback.ai_disclaimer && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
          ⓘ {feedback.ai_disclaimer}
        </Typography>
      )}
    </Box>
  )
}
